import React, { useCallback, useMemo, useRef, useState } from "react";

import { ColDef, ColGroupDef, ColumnState, GridApi } from "@ag-grid-community/core";
import invariant from "tiny-invariant";

import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import User from "@mapmycustomers/shared/types/User";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListRequest from "@mapmycustomers/shared/types/viewModel/internalModel/ListRequest";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";
import ListFetcher from "@app/types/viewModel/ListFetcher";
import convertFromAgGridColumnModel from "@app/util/viewModel/convertFromAgGridColumnModel";

import ViewContext from "../utils/ViewContext";

import { IAgGridListViewContext } from "./types/IListViewContext";
import IListViewComponent from "./types/ListViewComponent";

export interface AgGridListViewProviderProps<T extends AnyEntity> {
  children: React.ReactNode;
  columnDefs: ColDef[];
  columnToolPanelColumns?: (ColDef | ColGroupDef)[];
  entityType?: EntityType;
  fieldModel: IFieldModel;
  listFetcher: ListFetcher<T>;
  listViewState: ViewState;
  totalRecords?: number;
}

const AgGridListViewProvider = <T extends AnyEntity>({
  children,
  columnDefs,
  columnToolPanelColumns,
  entityType,
  fieldModel,
  listFetcher,
  listViewState,
  totalRecords,
}: AgGridListViewProviderProps<T>) => {
  const components = useRef<Set<IListViewComponent<T>>>(new Set());
  const [gridApi, setGridApi] = useState<GridApi | undefined>();

  const handleColumnModelChange = useCallback(
    (columnState: ColumnState[]) => {
      const columnModel = convertFromAgGridColumnModel(columnState, fieldModel);
      listFetcher({ request: { columns: columnModel }, updateOnly: true });
    },
    [fieldModel, listFetcher]
  );

  const handleExternalFilterModelChange = useCallback((filterModel: Partial<FilterModel>) => {
    Array.from(components.current).forEach((callback) => {
      callback.onFilterChange?.(filterModel);
    });
  }, []);

  const handleExternalColumnVisibilityChange = useCallback(
    (columnVisibilities: ColumnVisibility[]) => {
      Array.from(components.current).forEach((callback) => {
        callback.onColumnVisibilityChange?.(columnVisibilities);
      });
    },
    []
  );

  const handleUpdateEntity = useCallback((entity: T) => {
    Array.from(components.current).forEach((callback) => {
      callback.onUpdateEntity?.(entity);
    });
  }, []);

  const handleViewAsChange = useCallback(
    (userId: undefined | User["id"]) => {
      listFetcher({ request: { viewAs: userId }, updateOnly: true });
      gridApi?.onFilterChanged();
      Array.from(components.current).forEach((callback) => {
        callback.onViewAsChange?.(userId);
      });
    },
    [gridApi, listFetcher]
  );

  const handleRegister = useCallback(
    (component: IListViewComponent<T>) => {
      components.current.add(component);
    },
    [components]
  );

  const handleUnregister = useCallback(
    (component: IListViewComponent<T>) => {
      components.current.delete(component);
    },
    [components]
  );

  const handleUpdateListViewState = useCallback(
    (request: Partial<ListRequest>) => listFetcher({ request, updateOnly: true }),
    [listFetcher]
  );

  const handleUpdateListViewStateAndFetch = useCallback(
    (
      request: Partial<ListRequest>,
      dataCallback?: (response: ListResponse<T>) => void,
      failCallback?: () => void
    ) => listFetcher({ dataCallback, failCallback, request, updateOnly: false }),
    [listFetcher]
  );

  const context: IAgGridListViewContext<T> = useMemo(
    () => ({
      columnDefs,
      columnToolPanelColumns,
      entityType,
      fieldModel,
      goToPage: () => {
        invariant(false, "Not supported for AgGridListView");
      },
      gridApi,
      onColumnModelChange: handleColumnModelChange,
      onGridReady: setGridApi,
      register: handleRegister,
      totalRecords,
      unregister: handleUnregister,
      updateColumnVisibility: handleExternalColumnVisibilityChange,
      updateEntity: handleUpdateEntity,
      updateFilterModel: handleExternalFilterModelChange,
      updateViewAs: handleViewAsChange,
      updateViewState: handleUpdateListViewState,
      updateViewStateAndFetch: handleUpdateListViewStateAndFetch,
      viewState: listViewState,
    }),
    [
      columnDefs,
      columnToolPanelColumns,
      entityType,
      fieldModel,
      gridApi,
      handleColumnModelChange,
      handleExternalFilterModelChange,
      handleExternalColumnVisibilityChange,
      handleUpdateEntity,
      handleViewAsChange,
      handleRegister,
      handleUnregister,
      handleUpdateListViewState,
      handleUpdateListViewStateAndFetch,
      listViewState,
      totalRecords,
      setGridApi,
    ]
  );

  return (
    <ViewContext.Provider
      // Ignored because TS can't be sure that the Provider was created for exactly same
      // entity type as T. Although we know that it will be.
      // @ts-ignore
      value={context}
    >
      {children}
    </ViewContext.Provider>
  );
};

export default AgGridListViewProvider;
